<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Design system components
	import { Alert, Button, Checkbox, Radio, Text, toast } from '@Shelly/shelly-design-system';
	//Services
	import { format } from 'svelte-i18n';
	import { localReq, postReq } from '../../../../services/http';
	//Helpers
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import { capFirstChar, formatSnakeCaseToTitle } from '../../../../helpers/utilities';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device;

	const id = real_id(device.id);

	let settings = readDeviceSettings(id);
	let mode = settings.rollers[0].safety_mode;
	let command = settings.rollers[0].safety_allowed_on_trigger;
	let action = settings.rollers[0].safety_action;
	let cacheMode = mode;
	let cacheCommand = command;
	let cacheAction = action;
	let loading = false;

	$: settingsStore = getDeviceSettings(id);
	$: unavailable = $settingsStore.rollers[0].input_mode === 'openclose';
	$: disabled = mode === 'disabled';

	let commandOptions = ['none', 'open', 'close', 'all'];
	let commandOptI18n = [
		$format('option_command_none'),
		$format('option_command_open'),
		$format('option_command_close'),
		$format('option_command_all'),
	];

	//TODO: need refactoring after handle SelectHolder
	let modeOptions = ['disabled', 'while_opening', 'while_closing', 'while_moving'];
	let modeOptionsI18n = [
		$format('option_disabled'),
		$format('option_on_opening'),
		$format('option_on_closing'),
		$format('option_on_moving'),
	];
	let actionOptions = ['stop', 'pause', 'reverse'];

	let actionsOptionsI18n = [
		$format('option_stop'),
		$format('option_pause'),
		$format('option_reverse'),
	];

	let local_ip = get_local_ip(id);

	const setObstacle = async () => {
		if (cacheMode !== mode || cacheCommand !== command || cacheAction !== action) {
			let res;
			loading = true;
			let data = {
				safety_mode: mode,
				safety_action: action,
				safety_allowed_on_trigger: command,
				roller_id: 0,
				id: id,
			};

			if (local_ip) {
				delete data.id;
				delete data.roller_id;
				res = await localReq(device, `${local_ip}/settings/roller/0`, 'post', data);
			} else {
				res = await postReq(`/device/relay/roller/settings/safety`, data);
			}

			if (res.isok) {
				settings.rollers[0].safety_mode = mode;
				settings.rollers[0].safety_allowed_on_trigger = command;
				settings.rollers[0].safety_action = action;
				devices_settings.update_devices_settings(id, settings);
				toast.open({
					type: 'success',
					title: disabled
						? $format('safety_switch_disable')
						: $format('safety_switch_scc_toat', {
								values: { value: modeOptionsI18n[modeOptions.indexOf(mode)] },
							}) ||
							`${capFirstChar(device.type)} obstacle detection mode was ${
								!disabled ? 'changed to ' + formatSnakeCaseToTitle(mode) : 'disabled'
							}.`,
				});
				cacheMode = mode;
				cacheCommand = command;
				cacheAction = action;
			} else {
				mode = cacheMode;
				command = cacheCommand;
				action = cacheAction;
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			loading = false;
		}
	};

	const handleMode = (e) => {
		if (e === 0) {
			if (!disabled) {
				mode = modeOptions[e];
			} else {
				mode = modeOptions[1];
			}
		} else {
			mode = modeOptions[e];
		}
	};

	const handleAction = (e) => {
		action = actionOptions[e];
	};

	const handleCommand = (e) => {
		command = commandOptions[e];
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && !unavailable) {
			stickyBottomAction.set({ onClick: setObstacle, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if unavailable}
	<Alert
		type="info"
		isHeadingBold
		heading={$format('label_info')}
		message={$format('safety_switch_alert')}
		class="mb-s"
	/>
{:else}
	<Checkbox
		size="m"
		checked={mode !== modeOptions[0]}
		on:click={() => handleMode(0)}
		id="checkbox-ro8fiu0">{$format('label_enable')}</Checkbox
	>
	<Text styleType="body-small" class="mb-s">
		{$format('select_safety_switch_mode')}
	</Text>
	{#each modeOptions as _mode, index}
		{#if index !== 0}
			<Radio
				size="m"
				{disabled}
				selected={mode === modeOptions[index]}
				value={true}
				on:click={() => handleMode(index)}
				id="radio-368g3ln">{modeOptionsI18n[index]}</Radio
			>
		{/if}
	{/each}

	<Text styleType="body-small" class="mb-s">
		{$format('safety_switch_action')}
	</Text>
	{#each actionOptions as _action, index}
		<Radio
			size="m"
			{disabled}
			selected={action === actionOptions[index]}
			value={true}
			on:click={() => handleAction(index)}
			id="radio-ze2drru">{actionsOptionsI18n[index]}</Radio
		>
	{/each}
	<Text styleType="body-small" class="mb-s">
		{$format('safety_switch_allowed_commands')}
	</Text>
	{#each commandOptions as _command, index}
		<Radio
			{disabled}
			size="m"
			selected={command === commandOptions[index]}
			value={true}
			on:click={() => handleCommand(index)}
			id="radio-gz9t148">{commandOptI18n[index]}</Radio
		>
	{/each}
	{#if !$showNewLayout}
		<Button class="mt-m" {loading} disabled={loading} size="m" width="full" on:click={setObstacle}>
			{$format('button_save')}
		</Button>
	{/if}
{/if}
